"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const serviceOptions = ["Enterprise VPN Solutions", "Secure Remote Access", "Privacy Consulting", "Network Security Audit", "Custom Infrastructure"];
function DispatchForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="relative p-10 border border-gray-200 bg-white shadow-md rounded-md">
        <span className="absolute top-2 left-2 text-blue-600 font-mono text-xs">+</span>
        <span className="absolute top-2 right-2 text-blue-600 font-mono text-xs">+</span>
        <span className="absolute bottom-2 left-2 text-blue-600 font-mono text-xs">+</span>
        <span className="absolute bottom-2 right-2 text-blue-600 font-mono text-xs">+</span>
        <p className="font-default text-xs uppercase tracking-[0.18em] text-blue-600 mb-4">
          § STATUS / TRANSMITTED
        </p>
        <h3 className="font-default text-3xl font-bold text-gray-900 mb-4">
          Request <em className="text-blue-600 not-italic" style={{
          fontStyle: "italic"
        }}>received</em>.
        </h3>
        <p className="text-gray-700 mb-6 leading-relaxed">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-gray-300 bg-transparent text-gray-900 hover:bg-blue-600 hover:text-white hover:border-blue-600 font-default text-xs uppercase tracking-[0.16em] rounded-md">
          Send Another Request
        </Button>
      </div>;
  }
  return <div className="relative bg-white border border-gray-200 p-6 md:p-10 shadow-md rounded-md">
      <span className="absolute top-2 left-2 text-blue-600 font-mono text-sm leading-none select-none">+</span>
      <span className="absolute top-2 right-2 text-blue-600 font-mono text-sm leading-none select-none">+</span>
      <span className="absolute bottom-2 left-2 text-blue-600 font-mono text-sm leading-none select-none">+</span>
      <span className="absolute bottom-2 right-2 text-blue-600 font-mono text-sm leading-none select-none">+</span>

      <div className="mb-8 flex items-center gap-3">
        <span className="block h-px w-12 bg-blue-600"></span>
        <span className="block h-1 w-1 bg-blue-600"></span>
        <p className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-blue-600">
          § CONTACT FORM — INTAKE
        </p>
      </div>

      <Form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Min 2 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-600">
                  Full Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="John Smith" {...field} className="bg-gray-50 border border-gray-200 px-3 py-3 h-auto text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-blue-600 rounded-md" />
                </FormControl>
                <FormMessage className="font-default text-xs" />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-600">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} className="bg-gray-50 border border-gray-200 px-3 py-3 h-auto text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-blue-600 rounded-md" />
                </FormControl>
                <FormMessage className="font-default text-xs" />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^[+\d\s().-]{7,}$/,
            message: "Invalid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-600">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="bg-gray-50 border border-gray-200 px-3 py-3 h-auto text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-blue-600 rounded-md" />
                </FormControl>
                <FormMessage className="font-default text-xs" />
              </FormItem>} />

          <FormField control={control} name="service_type" rules={{
          required: "Please select a service"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-600">
                  Service Required
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="bg-gray-50 border border-gray-200 px-3 py-3 h-auto w-full text-gray-900 focus-visible:ring-0 focus-visible:border-blue-600 rounded-md">
                      <SelectValue placeholder="SELECT SERVICE" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 text-gray-900">
                      {serviceOptions.map(option => <SelectItem key={option} value={option} className="font-default text-xs uppercase tracking-[0.1em] focus:bg-blue-600 focus:text-white">
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage className="font-default text-xs" />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-600">
                Project Details
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your security needs..." rows={5} {...field} className="bg-gray-50 border border-gray-200 px-3 py-3 text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-blue-600 rounded-md resize-none" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        {submitError && <div className="p-4 border border-red-600 bg-red-50">
            <p className="text-red-600 font-default text-xs uppercase tracking-[0.12em]">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white hover:bg-blue-700 font-default text-xs uppercase tracking-[0.16em] py-6 h-auto group rounded-md">
          {isSubmitting ? <span>Sending...</span> : <>
              <span>Submit Request</span>
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </>}
        </Button>

        <p className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500 text-center pt-2">
          Your privacy is our priority. Data is encrypted.
        </p>
      </Form>
    </div>;
}
export default function Contact() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1666" id="contact" className="bg-white text-gray-900 py-24 md:py-44 px-6 md:px-22 relative overflow-hidden">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div ref={leftRef} className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start relative transition-all duration-700" style={{
        opacity: leftVisible ? 1 : 0,
        transform: leftVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <div aria-hidden="true" className="absolute -top-16 -left-4 font-default font-bold text-gray-100 select-none pointer-events-none leading-none" style={{
          fontSize: "28vw"
        }}>
            01
          </div>

          <div className="relative">
            <div className="mb-8 flex items-center gap-3">
              <span className="block h-px w-20 bg-blue-600"></span>
              <span className="block h-1 w-1 bg-blue-600"></span>
            </div>
            <p className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-blue-600 mb-8">
              § 01 / INTAKE — CONTACT FORM
            </p>

            <h1 className="font-default font-bold text-gray-900 leading-[0.94] tracking-[-0.025em] mb-8" style={{
            fontSize: "clamp(2.75rem, 7.2vw, 6.5rem)"
          }}>
              Get in{" "}
              <em className="text-blue-600" style={{
              fontStyle: "italic"
            }}>
                Touch
              </em>
            </h1>

            <p className="text-gray-700 leading-relaxed mb-12 max-w-md" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
          }}>
              Connect with our security experts to discuss your digital sovereignty and infrastructure needs.
            </p>

            <div className="space-y-5 border-t border-gray-200 pt-8">
              <div className="flex items-baseline gap-4">
                <span className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500 min-w-[110px]">
                  Support
                </span>
                <span className="font-default text-sm uppercase tracking-[0.1em] text-gray-900">
                  +1 (800) 555-0199
                </span>
              </div>
              <div className="flex items-baseline gap-4">
                <span className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500 min-w-[110px]">
                  Email
                </span>
                <span className="font-default text-sm uppercase tracking-[0.1em] text-gray-900 break-all">
                  security@vpngenix.com
                </span>
              </div>
              <div className="flex items-baseline gap-4">
                <span className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500 min-w-[110px]">
                  Availability
                </span>
                <span className="font-default text-sm uppercase tracking-[0.1em] text-gray-900">24 / 7</span>
              </div>
            </div>
          </div>
        </div>

        <div ref={rightRef} className="lg:col-span-7 transition-all duration-700 delay-100" style={{
        opacity: rightVisible ? 1 : 0,
        transform: rightVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <FormProvider formKey="contact_form_cybersecurity_cyber_genix" formId="contact_form_cybersecurity_cyber_genix" sectionName="contact-us" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          service_type: "",
          message: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          service_type: {
            required: true
          }
        }} submitText="Submit Request" successMessage="Your request has been received. Our team will contact you shortly." errorMessage="Transmission failed. Please try again later.">
            <DispatchForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}